import {
  Box,
  Button,
  CircularProgress,
  Grid2 as Grid,
  Icon,
  InputLabel,
  Typography,
} from "@mui/material";
import {
  handleRetrieveForum,
  handleUpdateForum,
} from "../../../redux/user-slices/forum.slice";
import { handleRetrieveForumCategories } from "../../../redux/public-slices/forum.slice";
import { checkUserType, FILE_PATH_URI } from "../../../utils/helper";
import { createForumSchema } from "../../../utils/velidations";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import SkeletonImage from "../../ui/SkeletonImage";
import InputFieldB from "../../ui/InputFieldB";
import SelectInput from "../../ui/SelectInput";
import Icons from "../../../assets/js/Icons";

function EditForum() {
  const files = useRef("");
  const formRef = useRef("");
  const navigate = useNavigate();
  const categoryRef = useRef("");
  const dispatch = useDispatch();
  const userType = checkUserType();
  const { id: forumId } = useParams();
  const [forum, setForum] = useState({});
  const [image, setImage] = useState(null);
  const [loading, setloading] = useState(null);
  const [categories, setCategories] = useState([]);
  const [helperText, setHelperText] = useState([]);
  const { loadings } = useSelector((state) => state.otherSlice);

  // Handle image preview
  const handleImagePreview = (e) => {
    files.current = Array.from(e.target.files);
    const views = files.current?.map((img) => URL.createObjectURL(img));
    setImage(views);
  };

  // Handle edit forum
  const handleSubmit = (isDraft) => {
    const form = formRef.current;
    const formData = Object.fromEntries(new FormData(form));
    formData.isDraft = isDraft;
    formData.banner = files?.current || [forum?.banner] || [];
    formData.categoryId = categoryRef?.current?.value?._id || "";

    const result = createForumSchema.safeParse(formData);
    if (!result.success) {
      const groupedErrors = {};
      result.error.issues.forEach((issue) => {
        const key = issue.path[0];
        if (!groupedErrors[key]) {
          groupedErrors[key] = [];
        }
        groupedErrors[key].push(issue.message);
      });
      setHelperText(groupedErrors);
      return;
    }
    setHelperText([]);
    dispatch(
      handleUpdateForum({
        id: forumId,
        formData,
      })
    )
      .unwrap()
      .then(() => {
        setTimeout(() => {
          navigate(`/${userType}/profile/all-forum`);
        }, 500);
      })
      .catch(() => {});
  };

  const retrieveData = async () => {
    const [categories, { forum }] = await Promise.all([
      dispatch(handleRetrieveForumCategories({ limit: 100 })).unwrap(),
      dispatch(handleRetrieveForum(forumId)).unwrap(),
    ]);
    const forumCategory = forum
      ? categories?.records?.find(({ _id }) => _id === forum?.categoryId)
      : "";
    if (forum) {
      forum.forumCategory = forumCategory;
    }
    setForum(forum);
    setImage(FILE_PATH_URI + forum?.banner?.path);
    setCategories(categories?.records);
    setloading(true);
  };

  useEffect(() => {
    retrieveData();

    // eslint-disable-next-line
  }, []);

  return (
    loading && (
      <Box
        sx={{
          p: 3,
          height: "100%",
          bgcolor: "white",
          borderRadius: "8px",
        }}
      >
        <Typography variant="dashboardSectionTitle">Add new forum</Typography>

        <Box className="page-content">
          <Grid container spacing={3} component="form" ref={formRef}>
            <Grid size={{ xs: 12, xl: 6 }}>
              <SelectInput
                label="Category *"
                renderValueKey="name"
                inputRef={categoryRef}
                options={categories || []}
                placeholder="Enter Question"
                selectLabel="Select Category"
                isError={Boolean(helperText?.categoryId)}
                defaultValue={forum?.forumCategory || "[object Object]"}
              />
              {helperText?.categoryId?.[0] && (
                <Typography mt={0.5} fontSize="14px" color="error.main">
                  {helperText?.categoryId?.[0]}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, xl: 6 }}>
              <InputFieldB
                name="title"
                label="Title *"
                variant="outlined"
                helperText={helperText}
                placeholder="Enter Title"
                labelvariant="InputLabelA"
                defaultValue={forum?.title || ""}
              />
            </Grid>
            <Grid size={12}>
              <InputLabel
                sx={{
                  mb: 0,
                  height: "100%",
                  maxHeight: 300,
                  borderRadius: "8px",
                  border: "1px dashed",
                  placeContent: "center",
                  borderColor: "primary.main",
                  display: image ? "block" : "grid",
                }}
              >
                {image ? (
                  <SkeletonImage
                    sx={{ objectFit: "contain" }}
                    src={image || null}
                  />
                ) : (
                  <>
                    <Icon
                      sx={{
                        fontSize: "100px",
                        color: "other.color2",
                        m: "auto",
                        svg: { display: "block" },
                      }}
                    >
                      <Icons.ImageSolid />
                    </Icon>
                    <Typography mb={1.5} color="other.color2">
                      Upload banner image
                    </Typography>
                  </>
                )}
                <input
                  style={{ display: "none" }}
                  onChange={handleImagePreview}
                  accept="image/*"
                  type="file"
                />
                {/*  */}
              </InputLabel>
              {helperText?.banner?.[0] && (
                <Typography mt={0.5} fontSize="14px" color="error.main">
                  {helperText?.banner?.[0]}
                </Typography>
              )}
            </Grid>
            <Grid size={12}>
              <InputFieldB
                type="textarea"
                label="content *"
                name="description"
                variant="outlined"
                helperText={helperText}
                labelvariant="InputLabelA"
                placeholder="Enter content"
                defaultValue={forum?.description || ""}
              />
            </Grid>
            <Grid size={12}>
              <Box
                sx={{
                  gap: 1,
                  maxWidth: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Button
                  type="button"
                  variant="contained"
                  sx={{
                    minWidth: "150px",
                    justifyContent: "center",
                  }}
                  endIcon={
                    loadings?.create_publish && (
                      <CircularProgress size={22} color="text" thickness={6} />
                    )
                  }
                  onClick={() => {
                    handleSubmit(false);
                  }}
                >
                  Submit
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  sx={{
                    minWidth: "150px",
                    justifyContent: "center",
                  }}
                  endIcon={
                    loadings?.create_draft && (
                      <CircularProgress size={22} color="text" thickness={6} />
                    )
                  }
                  onClick={() => {
                    handleSubmit(true);
                  }}
                >
                  Save Draft
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    )
  );
}

export default EditForum;
